import { ApolloLink, HttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import ApolloLinkTimeout from "apollo-link-timeout";
const authOperations = /* @__PURE__ */ new Set([
  "CancelSubscription",
  "ChangeSubscription",
  "CheckCustomerConsents",
  "CreateEyeExam",
  "CreateStoredCardPayment",
  "CreateStoredSepaPaymentMethod",
  "findSubscription",
  "getSuspendSubscription",
  "getBillingAddress",
  "getChangeSubscriptionPreview",
  "getCurrentSubscriptionDetails",
  "getOptIns",
  "getPersonalDetails",
  "getSocialIdentity",
  "getPreferredStore",
  "GetPreferredStore",
  "getPreferredStoreId",
  "GetPreferredStoreId",
  "GetPreferredStoreWithInventory",
  "GetPreferredStoreCode",
  "getRecentOrders",
  "getPrescriptionDownloads",
  "getAppointmentById",
  "getMyAppointments",
  "getMySubscriptions",
  "getOrderDetails",
  "getMyOrderHistory",
  "getSubscriptionDeliveries",
  "getSubscriptionPayments",
  "getSubscriptionPrescription",
  "getSubscriptionPrescriptionProducts",
  "getSubscriptionNotifications",
  "getSubscriptionProductRateplans",
  "getSuspensionResumeDates",
  "previewChangeSubscription",
  "ResumeSubscription",
  "RetrySubscriptionPayment",
  "SaveNewConsent",
  "SuspendSubscription",
  "UpdateBillingAddress",
  "UpdateDeliveryAddress",
  "updateOptIns",
  "updatePersonalDetails",
  "UpdatePersonalDetails",
  "UpdateSubscriptionBillingAddress",
  "UpdateSubscriptionPrescription",
  "storedPaymentMethods",
  "ChangePaymentMethod",
  "completeChallengeFlowStoredPayment",
  "activateSocialAccount",
  "requestSocialLoginMerge",
  "getBookAppointmentUser",
  "MergeShoppinglists",
  "GetMyWishlist",
  "GetMyWishlistId",
  "GetMySubscriptions",
  "GetMySubscriptionDetails",
  "GetPrescriptionsFromOrders",
  "UpdateSubscriptionInterval",
  "UpdateSubscriptionBillingAddress",
  "UpdateSubscriptionDeliveryAddress",
  "PauseSubscription"
]);
const operationsWithOptionalToken = /* @__PURE__ */ new Set([
  "SubscribeNewsletter",
  "PlaceOrder",
  "UpsertShoppingList",
  "getShoppingListById",
  "GetShoppingListCount",
  "AddToShoppinglist",
  "RemoveFromShoppinglist",
  "CartExpressPaymentMethods"
]);
const authCallback = (options) => (operation, previousRequest) => new Promise(async (resolve, reject) => {
  const requiresAuth = operation.operationName && (authOperations.has(operation.operationName) || operationsWithOptionalToken.has(operation.operationName));
  if (!requiresAuth || !options.getToken) {
    return resolve(previousRequest);
  }
  try {
    const token = await options.getToken();
    resolve({
      headers: {
        ...previousRequest.headers,
        authorization: `Bearer ${token}`
      }
    });
  } catch (e) {
    if (operation.operationName && operationsWithOptionalToken.has(operation.operationName)) {
      return resolve(previousRequest);
    }
    reject(e);
  }
});
const authLink = (options) => setContext(authCallback(options));
const httpLink = (options) => new HttpLink({
  credentials: "same-origin",
  fetch,
  ...options
});
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => console.error(`[GraphQL error]: ${error.message}`));
  }
  if (networkError) {
    console.error("[Network error]:", networkError);
  }
});
const forwardHeaders = (operation, forward) => {
  const context = operation.getContext();
  const forwardHeaders2 = context.forwardHeaders;
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...forwardHeaders2 && forwardHeaders2
    }
  }));
  return forward(operation);
};
const forwardHeadersLink = () => new ApolloLink((operation, forward) => forwardHeaders(operation, forward));
const timeoutLink = (timeoutMs, statusCode) => new ApolloLinkTimeout(timeoutMs, statusCode);
const DEFAULT_RECAPTCHA_PROTECTED_OPERATIONS = /* @__PURE__ */ new Set(["SubscribeNewsletter"]);
const recaptchaCallback = (options) => async (operation, previousContext) => {
  const { operationName } = operation;
  if (!operationName) {
    return previousContext;
  }
  const protectedOperations = options.recaptcha?.protectedOperations || DEFAULT_RECAPTCHA_PROTECTED_OPERATIONS;
  const shouldProtect = protectedOperations.has(operationName);
  if (!shouldProtect || !options.getRecaptchaToken) {
    return previousContext;
  }
  try {
    const action = options.recaptcha?.defaultAction || `graphql_${operationName.toLowerCase()}`;
    const recaptchaToken = await options.getRecaptchaToken(action);
    if (options.recaptcha?.enableLogging) {
      console.log(
        `[reCAPTCHA] Generated token for operation "${operationName}" with action "${action}"`
      );
    }
    return {
      ...previousContext,
      headers: {
        ...previousContext.headers,
        "X-Recaptcha-Token": recaptchaToken,
        "X-Recaptcha-Action": action
      }
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown reCAPTCHA error";
    if (options.recaptcha?.enableLogging) {
      console.error(
        `[reCAPTCHA] Failed to generate token for operation "${operationName}":`,
        errorMessage
      );
    }
    return previousContext;
  }
};
const recaptchaLink = (options) => {
  return setContext(recaptchaCallback(options));
};
export {
  authCallback,
  authLink,
  errorLink,
  forwardHeaders,
  forwardHeadersLink,
  httpLink,
  recaptchaCallback,
  recaptchaLink,
  timeoutLink
};
//# sourceMappingURL=links.js.map
